import { Controller } from "@hotwired/stimulus"
import consumer from "../channels/consumer"

const MAX_BROADCAST_ORDERS = 10

export default class extends Controller {
  static targets = ["sessionToken", "statusText", "draftBox", "amountInput", "availableCoin", "tokenText", "authorizeButton", "setAmountButton", "vendorTipButton", "clientTipButton", "sendingOverlay", "sendingImage", "sendingText", "submitOrderButton", "incomingOrders", "incomingOrdersBody", "smsOrderAlerts", "smsOrderAlertsBody", "orderReadyCheckbox", "orderCompletedCheckbox", "clientOrderTracker", "clientOrderBody", "builderDialog"]
  static values = {
    addressId: Number,
    role: String,
    vendorSelected: Boolean,
    initialToken: String,
    initialSession: Object,
    mazaIconUrl: String,
    pinballImageUrl: String,
    insertcoinsImageUrl: String,
    pigeonImageUrl: String
  }

  connect() {
    this.pollTimer = null
    this.lastSession = null
    this.tokenLocked = false
    this.baseAmount = null
    if (this.roleValue === "receive") {
      this.enableSetAmountButton()
    } else {
      this.disableSetAmountButton()
    }
    this.disableVendorTipButtons()
    this.disableClientTipButtons()
    this.subscription = null
    this.pendingAmount = null
    this.sendingTimer = null
    this.sendingTimeouts = []
    this.sendingVisualActive = false
    this.joinInFlight = false
    this.lastJoinAt = 0
    this.lastJoinToken = null
    this.currentOrder = { lines: [], subtotal: 0, total: 0, coupon_codes: [] }
    this.vendorSubscription = null
    this.lastVendorAlertKey = null
    this.broadcastOrders = this.loadBroadcastOrders()

    if (this.hasInitialSessionValue && this.initialSessionValue && Object.keys(this.initialSessionValue).length) {
      if (this.initialSessionValue.token) this.setToken(this.initialSessionValue.token)
      this.renderSession(this.initialSessionValue)
      this.startPolling()
    } else if (this.initialTokenValue) {
      this.setToken(this.initialTokenValue)
      this.startPolling()
    } else if (this.hasSessionTokenTarget && this.sessionTokenTarget.value) {
      this.setToken(this.sessionTokenTarget.value)
      this.startPolling()
    } else if (this.roleValue === "pay") {
      this.restoreClientOrderLane()
    }

    if (this.roleValue === "receive") {
      this.loadPendingAmount()
      this.renderBroadcastOrders()
      this.primeAudioOnGesture()
      this.subscribeToVendorLane()
    }
  }

  disconnect() {
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.unsubscribe()
    this.unsubscribeVendorLane()
    this.hideSendingAnimation()
  }

  builderDialogTargetConnected(dialog) {
    requestAnimationFrame(() => {
      if (!dialog.isConnected || dialog.open) return

      try {
        if (typeof dialog.showModal === "function") {
          dialog.showModal()
        } else {
          dialog.setAttribute("open", "")
        }
      } catch (_) {
        // Turbo may disconnect a just-rendered shell before the animation frame.
      }
    })
  }

  closeBuilderDialog(event) {
    event?.preventDefault?.()
    this.navigateToBuilderCanvas(event?.currentTarget?.closest?.("dialog") || this.builderDialogTarget)
  }

  cancelBuilderDialog(event) {
    event?.preventDefault?.()
    this.navigateToBuilderCanvas(event.currentTarget)
  }

  closeBuilderDialogFromBackdrop(event) {
    if (event.target !== event.currentTarget) return

    event.preventDefault()
    this.navigateToBuilderCanvas(event.currentTarget)
  }

  skipLoadingOverlay(event) {
    if (event?.button !== 0 || event?.ctrlKey || event?.metaKey || event?.shiftKey || event?.altKey) return
    document.documentElement.dataset.skipNextOverlay = "true"
  }

  builderFormStarted(event) {
    const form = event.currentTarget
    form.setAttribute("aria-busy", "true")
    const status = form.querySelector("[data-builder-save-state]")
    if (status) status.textContent = "SAVING…"
  }

  builderFormEnded(event) {
    const form = event.currentTarget
    form.removeAttribute("aria-busy")
    const status = form.querySelector("[data-builder-save-state]")
    if (!status) return

    status.textContent = event.detail?.success === false ? "NOT SAVED — CHECK THE FORM" : "SAVED"
  }

  navigateToBuilderCanvas(dialog) {
    const closeUrl = dialog?.dataset?.builderCloseUrl
    if (!closeUrl) return

    document.documentElement.dataset.skipNextOverlay = "true"
    if (window.Turbo?.visit) {
      window.Turbo.visit(closeUrl, { action: "replace" })
    } else {
      window.location.assign(closeUrl)
    }
  }

  joinSession() {
    const token = this.readToken()
    if (!token) {
      this.setStatus("Scan or enter a Unique Trading ID first.")
      return
    }

    // Guard against scanner bursts and duplicate concurrent joins.
    const now = Date.now()
    if (this.joinInFlight) return
    if (this.lastJoinToken === token && (now - this.lastJoinAt) < 1200) return
    this.joinInFlight = true
    this.lastJoinAt = now
    this.lastJoinToken = token

    this.post(`/addresses/${this.addressIdValue}/pow_wow_join_session`, { token })
      .then((data) => {
        if (!data.success) throw new Error(data.error || "Unable to join session.")
        this.setToken(token)
        this.tokenLocked = true
        this.subscribeToSession(token)
        this.renderSession(data.session)
        this.startPolling()
        this.syncPendingAmountIfAny()
      })
      .catch((error) => this.setStatus(error.message))
      .finally(() => {
        this.joinInFlight = false
      })
  }

  onScan(event) {
    const scannedToken = (event.detail?.data || "").toString().trim()
    if (!scannedToken) return
    if (!/^[0-9a-f]{24}$/i.test(scannedToken)) return
    const isNewToken = scannedToken !== this.currentToken
    if (this.tokenLocked && !isNewToken) return

    if (this.roleValue === "receive" && isNewToken) {
      this.resetReceiveSessionState()
    }

    this.setToken(scannedToken)
    this.setStatus("QR detected. Connecting vendor...")
    this.joinSession()
  }

  setAmount() {
    const token = this.currentToken || this.lastSession?.token || this.readToken()
    const amount = this.hasAmountInputTarget ? this.amountInputTarget.value : ""
    const numeric = Number.parseFloat(amount)

    if (!Number.isFinite(numeric) || numeric <= 0) {
      this.setStatus("Enter an amount greater than 0.")
      return
    }

    if (!token) {
      this.pendingAmount = numeric
      this.persistPendingAmount(this.pendingAmount)
      this.setStatus("Amount saved. Now you can scan client's QR when ready to sync devises and start trade.")
      this.renderDraft(`
        <p><strong>STATUS:</strong> AMOUNT UPDATED</p>
        <p><strong>QUEUED AMOUNT:</strong> ${numeric.toFixed(5).replace(/\.?0+$/, "")}</p>
        <p><strong>NEXT:</strong> SCAN CLIENT QR TO START SESSION</p>
      `)
      return
    }

    this.post(`/addresses/${this.addressIdValue}/pow_wow_update_session`, { token, amount })
      .then((data) => {
        if (!data.success) throw new Error(data.error || "Unable to set amount.")
        this.pendingAmount = null
        this.clearPendingAmount()
        this.renderSession(data.session)
      })
      .catch((error) => this.setStatus(error.message))
  }

  tapKey(event) {
    if (!this.hasAmountInputTarget) return
    const key = event.currentTarget.dataset.key
    if (key === "<-") {
      this.amountInputTarget.value = this.amountInputTarget.value.slice(0, -1)
      return
    }
    if (key === "." && this.amountInputTarget.value.includes(".")) return
    this.amountInputTarget.value = `${this.amountInputTarget.value}${key}`
    this.onAmountInput()
  }

  onAmountInput() {
    if (!this.hasAmountInputTarget) return
    const parsed = Number.parseFloat(this.amountInputTarget.value)
    if (Number.isFinite(parsed) && parsed > 0) {
      this.baseAmount = parsed
      this.enableVendorTipButtons()
      if (this.roleValue === "receive" && !this.currentToken) this.persistPendingAmount(parsed)
    } else {
      this.baseAmount = null
      this.disableVendorTipButtons()
      if (this.roleValue === "receive" && !this.currentToken) this.clearPendingAmount()
    }
  }

  applyTip(event) {
    if (!this.hasAmountInputTarget) return
    const tipPercent = Number.parseFloat(event.currentTarget.dataset.tipPercent)
    if (!Number.isFinite(tipPercent)) return

    const base = Number.isFinite(this.baseAmount) && this.baseAmount > 0
      ? this.baseAmount
      : Number.parseFloat(this.amountInputTarget.value)

    if (!Number.isFinite(base) || base <= 0) {
      this.setStatus("Enter an amount before adding tip.")
      this.disableVendorTipButtons()
      return
    }

    const withTip = base * (1 + tipPercent / 100)
    this.amountInputTarget.value = withTip.toFixed(5).replace(/\.?0+$/, "")
    this.onAmountInput()
  }

  applyClientTip(event) {
    const token = this.currentToken || this.lastSession?.token || this.readToken()
    const tipPercent = Number.parseFloat(event.currentTarget.dataset.tipPercent)
    if (!token || !Number.isFinite(tipPercent)) return

    this.post(`/addresses/${this.addressIdValue}/pow_wow_update_session`, { token, tip_percent: tipPercent })
      .then((data) => {
        if (!data.success) throw new Error(data.error || "Unable to apply tip adjustment.")
        this.renderSession(data.session)
      })
      .catch((error) => this.setStatus(error.message))
  }

  syncOrderTotal(event) {
    if (this.roleValue !== "receive" || !this.hasAmountInputTarget) return

    const total = Number.parseFloat(event.detail?.total)
    if (!Number.isFinite(total)) return

    this.amountInputTarget.value = total.toFixed(5).replace(/\.?0+$/, "")
    this.onAmountInput()
  }

  syncOrderReceipt(event) {
    this.currentOrder = event.detail?.order || { lines: [], subtotal: 0, total: 0, coupon_codes: [] }
    this.updateSubmitOrderButton()
  }

  submitOrder() {
    const token = this.currentToken || this.lastSession?.token || this.readToken()
    if (!token) {
      this.setStatus("Open the client PAY screen first so the order has a live session.")
      return
    }

    if (!this.currentOrder?.lines?.length) {
      this.setStatus("Build a receipt before sending the order.")
      return
    }

    this.post(`/addresses/${this.addressIdValue}/pow_wow_update_session`, { token, order: this.currentOrder })
      .then((data) => {
        if (!data.success) throw new Error(data.error || "Unable to send order.")
        this.renderSession(data.session)
        this.authorizePayment(data.session)
      })
      .catch((error) => this.setStatus(error.message))
  }

  toggleOrderReady(event) {
    const token = this.currentToken || this.lastSession?.token || this.readToken()
    if (!token) return

    this.post(`/addresses/${this.addressIdValue}/pow_wow_update_session`, {
      token,
      order_ready: event.currentTarget.checked
    }).then((data) => {
      if (!data.success) throw new Error(data.error || "Unable to update order status.")
      this.renderSession(data.session)
    }).catch((error) => {
      event.currentTarget.checked = !event.currentTarget.checked
      this.setStatus(error.message)
    })
  }

  toggleOrderCompleted(event) {
    const token = this.currentToken || this.lastSession?.token || this.readToken()
    if (!token) return

    this.post(`/addresses/${this.addressIdValue}/pow_wow_update_session`, {
      token,
      order_completed: event.currentTarget.checked
    }).then((data) => {
      if (!data.success) throw new Error(data.error || "Unable to update delivery status.")
      this.renderSession(data.session)
    }).catch((error) => {
      event.currentTarget.checked = !event.currentTarget.checked
      this.setStatus(error.message)
    })
  }

  startPolling() {
    if (!this.currentToken) return
    if (this.pollTimer) clearInterval(this.pollTimer)

    const poll = () => {
      this.get(`/addresses/${this.addressIdValue}/pow_wow_session_status?token=${encodeURIComponent(this.currentToken)}`)
        .then((data) => {
          if (data.success) this.renderSession(data.session)
        })
        .catch(() => {})
    }

    poll()
    this.pollTimer = setInterval(poll, 2500)
  }

  subscribeToSession(token) {
    if (!token) return
    this.unsubscribe()

    this.subscription = consumer.subscriptions.create(
      { channel: "PowWowPayChannel", token },
      {
        received: (data) => {
          if (data?.session) this.renderSession(data.session)
        }
      }
    )
  }

  subscribeToVendorLane() {
    if (this.roleValue !== "receive") return
    this.unsubscribeVendorLane()

    this.vendorSubscription = consumer.subscriptions.create(
      { channel: "PowWowVendorChannel" },
      {
        received: (data) => {
          if (data?.order) {
            this.renderBroadcastOrder(data.order)
            return
          }
          if (!data?.session) return
          if (!this.sessionBelongsToReceiveAddress(data.session)) return
          if (data.session?.token) this.setToken(data.session.token)
          this.renderSession(data.session)
          if (this.shouldVendorAlert(data.session)) this.playVendorAlert()
        }
      }
    )
  }

  unsubscribeVendorLane() {
    if (!this.vendorSubscription) return
    consumer.subscriptions.remove(this.vendorSubscription)
    this.vendorSubscription = null
  }

  unsubscribe() {
    if (!this.subscription) return
    consumer.subscriptions.remove(this.subscription)
    this.subscription = null
  }

  renderSession(session) {
    if (!session) return
    this.lastSession = session
    this.renderIncomingOrder(session)
    this.renderClientOrder(session)
    this.updateSubmitOrderButton()
    this.persistClientOrderLane(session)

    if (this.hasAvailableCoinTarget && typeof session.client_available_coin !== "undefined") {
      this.availableCoinTarget.textContent = Number(session.client_available_coin).toFixed(5)
    }

    const vendorAlias = session.vendor_alias ? `@${session.vendor_alias}` : "vendor"
    const clientAlias = session.client_alias ? `@${session.client_alias}` : "client"

    if (!session.vendor_user_id) {
      if (this.roleValue === "receive") {
        this.enableSetAmountButton()
      } else {
        this.disableSetAmountButton()
      }
      this.hideAuthorizeButton()
      this.disableClientTipButtons()
      this.setStatus("🐦‍⬛ Awaiting trade connection...")
      if (session.amount) {
        const queuedAmount = Number(session.amount).toFixed(5)
        this.renderDraft(`
          <p><strong>Client:</strong> ${clientAlias}</p>
          <p><strong>Vendor:</strong> ${vendorAlias}</p>
          <p><strong>Amount:</strong> ${this.formatAmountWithCoin(queuedAmount, session.coin)}</p>
          ${this.renderOrderSummary(session)}
          ${session.adjustment_note ? `<p><strong>Note:</strong> ${session.adjustment_note}</p>` : ""}
        `)
      } else {
        this.renderDraft("")
      }
      return
    }

    this.enableSetAmountButton()

    if (!session.amount) {
      this.hideAuthorizeButton()
      this.disableClientTipButtons()
      this.setStatus(`Awaiting Payment Amount from ${vendorAlias}`)
      this.renderDraft(`Draft: ${clientAlias} paying ${vendorAlias}.`)
      return
    }

    const amount = Number(session.amount).toFixed(5)
    this.setStatus(`Draft Ready: ${clientAlias} -> ${vendorAlias} (${amount} ${session.coin})`)
    this.renderDraft(`
      <p><strong>Client:</strong> ${clientAlias}</p>
      <p><strong>Vendor:</strong> ${vendorAlias}</p>
      <p><strong>Amount:</strong> ${this.formatAmountWithCoin(amount, session.coin)}</p>
      ${this.renderOrderSummary(session)}
      ${session.adjustment_note ? `<p><strong>Note:</strong> ${session.adjustment_note}</p>` : ""}
    `)

    if (this.roleValue === "pay" && session.status !== "authorized" && session.status !== "completed") {
      this.showAuthorizeButton()
      this.enableClientTipButtons()
    } else {
      this.hideAuthorizeButton()
      this.disableClientTipButtons()
    }

    if (session.status === "review_ready") {
      this.hideAuthorizeButton()
      this.setStatus("Client review submitted. Awaiting final CONFIRM & PAY.")
      if (this.sendingVisualActive) {
        this.hideSendingAnimation()
        this.sendingVisualActive = false
      }
    }

    if (session.status === "order_submitted") {
      this.setStatus("Order submitted. Waiting for vendor pickup signal.")
    }

    if (session.status === "order_ready") {
      this.setStatus(this.roleValue === "pay" ? "Order ready for pickup." : "Order marked ready for pickup.")
    }

    if (session.status === "order_complete") {
      this.setStatus(this.roleValue === "pay" ? "Order complete / delivered." : "Order marked complete.")
    }

    if (session.status === "authorized") {
      this.hideAuthorizeButton()
      this.setStatus(this.roleValue === "receive" ? "Payment incoming. Watch incoming orders for tx status." : "Payment authorized. Sending transaction...")
      if (!this.sendingVisualActive) {
        this.showSendingAnimation()
        this.sendingVisualActive = true
      }
    }

    if (session.status === "completed" && session.tx_id) {
      if (this.sendingVisualActive) {
        this.hideSendingAnimation()
        this.sendingVisualActive = false
      }
      if (this.roleValue === "receive") {
        this.tokenLocked = false
      }
      this.setStatus("Payment sent.")
      this.renderDraft(`
        ${this.renderOrderSummary(session)}
        <p><strong>TXID:</strong> <a href="${this.transactionExplorerUrl(session)}" target="_blank" rel="noopener noreferrer">${session.tx_id}</a></p>
      `)
    }
  }

  renderIncomingOrder(session) {
    const order = session?.order
    const clientAlias = session?.client_alias ? `@${session.client_alias}` : "client"

    if (this.hasIncomingOrdersTarget) {
      this.incomingOrdersTarget.hidden = !(this.roleValue === "receive" && order?.lines?.length)
    }

    if (this.hasIncomingOrdersBodyTarget) {
      if (!(this.roleValue === "receive" && order?.lines?.length)) {
        this.incomingOrdersBodyTarget.innerHTML = ""
      } else {
        const coupons = (order.coupon_codes || []).length ? `<p class="pow-wow-order-card-minor">coupons // ${(order.coupon_codes || []).map((code) => this.escapeHtml(code)).join(", ")}</p>` : ""
        const clientLine = `<div class="pow-wow-order-ledger-line"><span>client</span><strong>${this.escapeHtml(clientAlias)}</strong></div>`
        const txLine = session.tx_id ? `<div class="pow-wow-order-ledger-line"><span>tx</span><strong><a href="${this.transactionExplorerUrl(session)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(session.tx_id)}</a></strong></div>` : ""
        const statusLine = `<div class="pow-wow-order-ledger-line"><span>status</span><strong>${this.escapeHtml(this.humanOrderStatus(session))}</strong></div>`
        this.incomingOrdersBodyTarget.innerHTML = `
          <div class="pow-wow-order-card pow-wow-order-card-vendor">
            <div class="pow-wow-order-checklist">
              ${(order.lines || []).map((line) => `
                <div class="pow-wow-order-check">
                  <span class="pow-wow-order-check-box" aria-hidden="true"></span>
                  <div class="pow-wow-order-check-copy">
                    <strong>${line.quantity}x ${this.escapeHtml(line.name)}</strong>
                    ${(line.options || []).length ? `
                      <div class="pow-wow-order-detail-list">
                        ${line.options.map((option) => `
                          <div class="pow-wow-order-detail">
                            <span class="pow-wow-order-detail-box" aria-hidden="true"></span>
                            <span>${this.formatOrderOption(option)}</span>
                          </div>
                        `).join("")}
                      </div>
                    ` : `<small>base build</small>`}
                  </div>
                </div>
              `).join("")}
            </div>
            ${coupons}
            <div class="pow-wow-order-ledger">
              ${clientLine}
              ${statusLine}
              <div class="pow-wow-order-ledger-line"><span>subtotal</span><strong>${this.formatAmountWithCoin(order.subtotal, session.coin)}</strong></div>
              <div class="pow-wow-order-ledger-line"><span>checkout</span><strong>${this.formatAmountWithCoin(order.total, session.coin)}</strong></div>
              ${txLine}
            </div>
          </div>
        `
      }
    }

    if (this.hasOrderReadyCheckboxTarget && this.roleValue === "receive") {
      this.orderReadyCheckboxTarget.disabled = false
      this.orderReadyCheckboxTarget.checked = !!order?.ready
    }

    if (this.hasOrderCompletedCheckboxTarget && this.roleValue === "receive") {
      this.orderCompletedCheckboxTarget.disabled = false
      this.orderCompletedCheckboxTarget.checked = !!order?.completed
    }
  }

  renderBroadcastOrder(order) {
    if (this.roleValue !== "receive") return
    const sanitized = this.sanitizeBroadcastOrder(order)
    if (!sanitized) return

    const isNew = !this.broadcastOrders.some((saved) => saved.public_code === sanitized.public_code)
    this.broadcastOrders = [
      sanitized,
      ...this.broadcastOrders.filter((saved) => saved.public_code !== sanitized.public_code)
    ].slice(0, MAX_BROADCAST_ORDERS)
    this.persistBroadcastOrders()
    this.renderBroadcastOrders()

    this.setStatus(`New ${sanitized.source.toUpperCase()} order #${sanitized.public_code}`)
    if (isNew) this.playVendorAlert()
  }

  renderBroadcastOrders() {
    const shouldShow = this.roleValue === "receive" && this.broadcastOrders.length > 0
    if (this.hasSmsOrderAlertsTarget) this.smsOrderAlertsTarget.hidden = !shouldShow
    if (!this.hasSmsOrderAlertsBodyTarget) return

    if (!shouldShow) {
      this.smsOrderAlertsBodyTarget.innerHTML = ""
      return
    }

    this.smsOrderAlertsBodyTarget.innerHTML = this.broadcastOrders.map((order) => `
        <div class="pow-wow-order-card pow-wow-order-card-vendor">
          <div class="pow-wow-order-status-banner">${this.escapeHtml(String(order.source || "sms").toUpperCase())} ORDER #${this.escapeHtml(order.public_code)}</div>
          <div class="pow-wow-order-checklist">
            ${(order.lines || []).map((line) => `
              <div class="pow-wow-order-check">
                <span class="pow-wow-order-check-box" aria-hidden="true"></span>
                <div class="pow-wow-order-check-copy">
                  <strong>${Number(line.quantity) || 1}x ${this.escapeHtml(line.name || "Menu item")}</strong>
                  ${(line.options || []).length ? `<small>${line.options.map((option) => this.formatOrderOption(option)).join(" // ")}</small>` : `<small>base build</small>`}
                </div>
              </div>
            `).join("")}
          </div>
          <div class="pow-wow-order-ledger">
            <div class="pow-wow-order-ledger-line"><span>status</span><strong>${this.escapeHtml(order.status || "submitted")}</strong></div>
            <div class="pow-wow-order-ledger-line"><span>checkout</span><strong>${this.formatAmountWithCoin(order.total, order.currency)}</strong></div>
          </div>
          <p class="pow-wow-order-card-meta">Saved safely. Refresh Recent Orders to open the full merchant workflow.</p>
        </div>
      `).join("")
  }

  sanitizeBroadcastOrder(order) {
    if (!order || !Array.isArray(order.lines) || !order.lines.length) return null
    const merchantAddressId = Number(order.merchant_address_id)
    if (!Number.isInteger(merchantAddressId) || merchantAddressId !== this.addressIdValue) return null
    const publicCode = String(order.public_code || "").trim().slice(0, 64)
    if (!publicCode) return null
    const currency = String(order.currency || "").trim().toUpperCase()
    const total = Number(order.total)

    return {
      public_code: publicCode,
      merchant_address_id: merchantAddressId,
      connector_id: String(order.connector_id || "").trim().slice(0, 64),
      source: String(order.source || "sms").trim().slice(0, 20) || "sms",
      status: String(order.status || "submitted").trim().slice(0, 30) || "submitted",
      currency: /^[A-Z0-9_-]{1,12}$/.test(currency) ? currency : "",
      total: Number.isFinite(total) && total >= 0 ? total : 0,
      created_at: String(order.created_at || "").slice(0, 40),
      lines: order.lines.slice(0, 20).map((line) => ({
        quantity: Math.max(1, Math.min(25, Math.floor(Number(line?.quantity) || 1))),
        name: String(line?.name || "Menu item").slice(0, 120),
        options: (Array.isArray(line?.options) ? line.options : []).slice(0, 20).map((option) => ({
          group_name: String(option?.group_name || "").slice(0, 80),
          name: String(option?.name || "").slice(0, 120),
          position_name: String(option?.position_name || "").slice(0, 80)
        }))
      }))
    }
  }

  sessionBelongsToReceiveAddress(session) {
    if (this.roleValue !== "receive") return true
    const vendorAddressId = Number(session?.vendor_address_id || session?.intended_vendor_address_id)
    return Number.isInteger(vendorAddressId) && vendorAddressId === this.addressIdValue
  }

  broadcastOrderStorageKey() {
    return `pow_wow_sms_order_alerts_${this.addressIdValue}`
  }

  loadBroadcastOrders() {
    if (this.roleValue !== "receive") return []

    try {
      const raw = window.sessionStorage.getItem(this.broadcastOrderStorageKey())
      const stored = raw ? JSON.parse(raw) : []
      if (!Array.isArray(stored)) return []
      return stored
        .map((order) => this.sanitizeBroadcastOrder(order))
        .filter(Boolean)
        .slice(0, MAX_BROADCAST_ORDERS)
    } catch (_error) {
      try {
        window.sessionStorage.removeItem(this.broadcastOrderStorageKey())
      } catch (_storageError) {
        // Storage may be disabled by the browser.
      }
      return []
    }
  }

  persistBroadcastOrders() {
    try {
      window.sessionStorage.setItem(
        this.broadcastOrderStorageKey(),
        JSON.stringify(this.broadcastOrders.slice(0, MAX_BROADCAST_ORDERS))
      )
    } catch (_error) {
      // The durable server-side order still appears in Recent Orders.
    }
  }

  renderClientOrder(session) {
    if (!this.hasClientOrderTrackerTarget || !this.hasClientOrderBodyTarget) return
    const order = session?.order
    const shouldShow = this.roleValue === "pay" && order?.lines?.length
    this.clientOrderTrackerTarget.hidden = !shouldShow
    if (!shouldShow) {
      this.clientOrderBodyTarget.innerHTML = ""
      return
    }

    const vendorAlias = session?.vendor_alias || session?.intended_vendor_alias ? `@${session.vendor_alias || session.intended_vendor_alias}` : "vendor"
    const coupons = (order.coupon_codes || []).length ? `<div class="pow-wow-order-ledger-line"><span>coupons</span><strong>${(order.coupon_codes || []).map((code) => this.escapeHtml(code)).join(", ")}</strong></div>` : ""
    const vendorLine = `<div class="pow-wow-order-ledger-line"><span>vendor</span><strong>${this.escapeHtml(vendorAlias)}</strong></div>`
    const txLine = session.tx_id ? `<div class="pow-wow-order-ledger-line"><span>tx</span><strong><a href="${this.transactionExplorerUrl(session)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(session.tx_id)}</a></strong></div>` : ""
    this.clientOrderBodyTarget.innerHTML = `
      <div class="pow-wow-order-card pow-wow-order-card-client">
        <div class="pow-wow-order-status-banner">${this.escapeHtml(this.humanOrderStatus(session))}</div>
        <div class="pow-wow-order-client-list">
          ${(order.lines || []).map((line) => `
            <div class="pow-wow-order-client-item">
              <strong>${line.quantity}x ${this.escapeHtml(line.name)}</strong>
              <small>${(line.options || []).length ? line.options.map((option) => this.formatOrderOption(option)).join(" // ") : "base build"}</small>
            </div>
          `).join("")}
        </div>
        <div class="pow-wow-order-ledger">
          ${vendorLine}
          <div class="pow-wow-order-ledger-line"><span>subtotal</span><strong>${this.formatAmountWithCoin(order.subtotal, session.coin)}</strong></div>
          <div class="pow-wow-order-ledger-line"><span>checkout</span><strong>${this.formatAmountWithCoin(order.total, session.coin)}</strong></div>
          ${coupons}
          ${txLine}
        </div>
      </div>
    `
  }

  clientOrderStorageKey() {
    return `pow_wow_client_order_lane_${this.addressIdValue}`
  }

  persistClientOrderLane(session) {
    if (this.roleValue !== "pay") return
    const order = session?.order
    const delivered = !!order?.completed

    if (!order?.lines?.length || delivered) {
      window.localStorage.removeItem(this.clientOrderStorageKey())
      return
    }

    const vendorId = session?.intended_vendor_user_id || session?.vendor_user_id
    const vendorAddressId = session?.intended_vendor_address_id || session?.vendor_address_id
    if (!session?.token || !vendorId || !vendorAddressId) return

    window.localStorage.setItem(this.clientOrderStorageKey(), JSON.stringify({
      token: session.token,
      vendor_user_id: vendorId,
      vendor_address_id: vendorAddressId
    }))
  }

  restoreClientOrderLane() {
    try {
      const raw = window.localStorage.getItem(this.clientOrderStorageKey())
      if (!raw) return

      const stored = JSON.parse(raw)
      if (!stored?.token) return

      const url = new URL(window.location.href)
      url.searchParams.set("role", "pay")
      url.searchParams.set("token", stored.token)
      if (stored.vendor_user_id) url.searchParams.set("vendor_user_id", stored.vendor_user_id)
      if (stored.vendor_address_id) url.searchParams.set("vendor_address_id", stored.vendor_address_id)
      window.location.replace(url.toString())
    } catch (_error) {
      window.localStorage.removeItem(this.clientOrderStorageKey())
    }
  }

  shouldVendorAlert(session) {
    const status = session?.status
    if (!["order_submitted", "review_ready", "authorized", "completed", "order_complete"].includes(status)) return false

    const nextKey = [
      session?.token,
      status,
      session?.tx_id,
      session?.transaction_id,
      session?.updated_at
    ].filter(Boolean).join(":")

    if (this.lastVendorAlertKey === nextKey) return false
    this.lastVendorAlertKey = nextKey
    return true
  }

  primeAudioOnGesture() {
    const unlock = () => {
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext
        if (!AudioCtx) return
        if (!this.audioContext) this.audioContext = new AudioCtx()
        if (this.audioContext.state === "suspended") this.audioContext.resume()
      } catch (_error) {
      }
    }

    this._audioUnlockHandler = unlock
    window.addEventListener("pointerdown", unlock, { once: true })
    window.addEventListener("keydown", unlock, { once: true })
  }

  playVendorAlert() {
    if (this.roleValue !== "receive") return
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext
      if (!AudioCtx) return
      if (!this.audioContext) this.audioContext = new AudioCtx()
      const context = this.audioContext
      if (context.state === "suspended") context.resume()

      const now = context.currentTime
      const gain = context.createGain()
      gain.gain.setValueAtTime(0.0001, now)
      gain.gain.exponentialRampToValueAtTime(0.03, now + 0.02)
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.24)
      gain.connect(context.destination)

      const oscillator = context.createOscillator()
      oscillator.type = "square"
      oscillator.frequency.setValueAtTime(880, now)
      oscillator.frequency.setValueAtTime(660, now + 0.12)
      oscillator.connect(gain)
      oscillator.start(now)
      oscillator.stop(now + 0.24)
    } catch (_error) {
      // best-effort alert only
    }
  }

  updateSubmitOrderButton() {
    if (!this.hasSubmitOrderButtonTarget) return
    const hasLines = !!this.currentOrder?.lines?.length
    this.submitOrderButtonTarget.disabled = !(hasLines && this.vendorSelectedValue)
  }

  renderOrderSummary(session) {
    const order = session?.order
    if (!order?.lines?.length) return ""

    const lines = order.lines.map((line) => `
      <li>${line.quantity}x ${this.escapeHtml(line.name)}${(line.options || []).length ? ` // ${line.options.map((option) => this.formatOrderOption(option)).join(" // ")}` : ""}</li>
    `).join("")

    const couponLine = (order.coupon_codes || []).length ? `<p><strong>Coupons:</strong> ${(order.coupon_codes || []).map((code) => this.escapeHtml(code)).join(", ")}</p>` : ""
    const txLine = session.tx_id ? `<p><strong>TX:</strong> <a href="${this.transactionExplorerUrl(session)}" target="_blank" rel="noopener noreferrer">${this.escapeHtml(session.tx_id)}</a></p>` : ""

    return `
      <p><strong>Order Status:</strong> ${this.escapeHtml(this.humanOrderStatus(session))}</p>
      <ul>${lines}</ul>
      ${couponLine}
      <p><strong>Checkout:</strong> ${this.formatAmountWithCoin(order.total, session.coin)}</p>
      ${txLine}
    `
  }

  humanOrderStatus(session) {
    switch (session?.status) {
      case "order_submitted":
        return "order submitted"
      case "order_ready":
        return "ready for pickup"
      case "order_complete":
        return "complete / delivered"
      case "review_ready":
        return "payment review ready"
      case "authorized":
        return "payment sending"
      case "completed":
        return "paid"
      default:
        return session?.status || "draft"
    }
  }

  formatOrderOption(option) {
    const prefix = `${this.escapeHtml(option.group_name)}: ${this.escapeHtml(option.name)}`
    return option.position_name ? `${prefix} [${this.escapeHtml(option.position_name)}]` : prefix
  }

  authorizePayment(session) {
    this.showSendingAnimation()
    this.post(`/addresses/${this.addressIdValue}/pow_wow_authorize_payment`, { token: this.currentToken })
      .then((data) => {
        if (!data.success) throw new Error(data.error || "Unable to authorize payment.")
        window.location.href = data.redirect_to
      })
      .catch((error) => {
        this.hideSendingAnimation()
        this.setStatus(error.message)
      })
  }

  authorizeFromButton() {
    if (this.roleValue === "pay" && this.currentOrder?.lines?.length && !this.lastSession?.order?.lines?.length) {
      this.submitOrder()
      return
    }

    if (!this.lastSession) {
      this.setStatus("No active draft to authorize.")
      return
    }
    this.authorizePayment(this.lastSession)
  }

  readToken() {
    if (this.currentToken) return this.currentToken
    if (!this.hasSessionTokenTarget) return ""
    const token = this.sessionTokenTarget.value.trim()
    if (token) this.setToken(token)
    return token
  }

  setToken(token) {
    if (this.currentToken === token && this.subscription) {
      if (this.hasSessionTokenTarget) this.sessionTokenTarget.value = token
      if (this.hasTokenTextTarget) this.tokenTextTarget.textContent = token
      return
    }
    this.currentToken = token
    if (this.hasSessionTokenTarget) this.sessionTokenTarget.value = token
    if (this.hasTokenTextTarget) this.tokenTextTarget.textContent = token
    this.subscribeToSession(token)
  }

  resetReceiveSessionState() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
    this.unsubscribe()
    this.currentToken = null
    this.lastSession = null
    this.tokenLocked = false
    this.hideAuthorizeButton()
    this.disableClientTipButtons()
    this.renderDraft("")
  }

  setStatus(message) {
    if (this.hasStatusTextTarget) this.statusTextTarget.textContent = message
  }

  renderDraft(html) {
    if (this.hasDraftBoxTarget) this.draftBoxTarget.innerHTML = html
  }

  showAuthorizeButton() {
    if (this.hasAuthorizeButtonTarget) this.authorizeButtonTarget.style.display = "inline-block"
  }

  hideAuthorizeButton() {
    if (this.hasAuthorizeButtonTarget) this.authorizeButtonTarget.style.display = "none"
  }

  get(url) {
    return fetch(url, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    }).then((res) => res.json())
  }

  post(url, payload) {
    return this.request("POST", url, payload)
  }

  patch(url, payload) {
    return this.request("PATCH", url, payload)
  }

  request(method, url, payload) {
    const token = document.querySelector("meta[name='csrf-token']")?.content
    return fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": token
      },
      credentials: "same-origin",
      body: JSON.stringify(payload)
    }).then(async (res) => {
      const contentType = res.headers.get("content-type") || ""

      if (contentType.includes("application/json")) {
        const data = await res.json()
        if (!res.ok) {
          const loginHint = data?.login_url ? ` Login there: ${data.login_url}` : ""
          return { success: false, error: `${data?.error || `Request failed (${res.status})`}${loginHint}` }
        }
        return data
      }

      const text = await res.text()
      const challengePage = /just a moment/i.test(text) || /cf-browser-verification|cf-challenge|cloudflare/i.test(text)
      if (challengePage) {
        return { success: false, error: "Cloudflare check interrupted this background request. Refresh and try again." }
      }
      const fallbackMessage = res.ok ? "Unexpected response format." : `Request failed (${res.status}).`
      return { success: false, error: text?.slice(0, 180) || fallbackMessage }
    }).catch(() => ({ success: false, error: "Network/JSON error. Please retry." }))
  }

  enableSetAmountButton() {
    if (this.hasSetAmountButtonTarget) this.setAmountButtonTarget.disabled = false
  }

  disableSetAmountButton() {
    if (this.hasSetAmountButtonTarget) this.setAmountButtonTarget.disabled = true
  }

  enableTipButtons() {
    this.enableVendorTipButtons()
  }

  disableTipButtons() {
    this.disableVendorTipButtons()
  }

  enableVendorTipButtons() {
    if (!this.hasVendorTipButtonTarget) return
    this.vendorTipButtonTargets.forEach((button) => { button.disabled = false })
  }

  disableVendorTipButtons() {
    if (!this.hasVendorTipButtonTarget) return
    this.vendorTipButtonTargets.forEach((button) => { button.disabled = true })
  }

  enableClientTipButtons() {
    if (!this.hasClientTipButtonTarget) return
    this.clientTipButtonTargets.forEach((button) => { button.disabled = false })
  }

  disableClientTipButtons() {
    if (!this.hasClientTipButtonTarget) return
    this.clientTipButtonTargets.forEach((button) => { button.disabled = true })
  }

  formatAmountWithCoin(amount, coin) {
    if (coin === "MAZA" && this.hasMazaIconUrlValue) {
      return `${amount} ${coin} <img src="${this.mazaIconUrlValue}" alt="Maza" class="pow-inline-icon-sm">`
    }
    return `${amount} ${coin}`
  }

  transactionExplorerUrl(session) {
    const txId = String(session?.tx_id || "").trim()
    if (!txId) return "#"
    const coin = String(session?.coin || "MAZA").trim().toUpperCase()
    const paths = { MAZA: "", BTC: "/c/btc", LTC: "/c/ltc", DOGE: "/c/doge" }
    if (Object.hasOwn(paths, coin)) {
      // Rebuild known-coin links so old saved sessions also open Tokex.
      return `https://tokex.org${paths[coin]}/tx/${encodeURIComponent(txId)}`
    }
    return this.escapeHtml(session?.explorer_url || "#")
  }

  escapeHtml(value) {
    return String(value || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
  }

  pendingAmountStorageKey() {
    return `pow_wow_pending_amount_${this.addressIdValue}`
  }

  loadPendingAmount() {
    if (!this.hasAmountInputTarget) return
    const raw = window.localStorage.getItem(this.pendingAmountStorageKey())
    const value = Number.parseFloat(raw)
    if (!Number.isFinite(value) || value <= 0) return
    this.pendingAmount = value
    this.amountInputTarget.value = value.toFixed(5).replace(/\.?0+$/, "")
    this.onAmountInput()
    this.setStatus("Loaded saved amount. Scan client QR to sync.")
  }

  persistPendingAmount(amount) {
    if (!Number.isFinite(amount) || amount <= 0) return
    window.localStorage.setItem(this.pendingAmountStorageKey(), amount.toString())
  }

  clearPendingAmount() {
    window.localStorage.removeItem(this.pendingAmountStorageKey())
  }

  syncPendingAmountIfAny() {
    const amount = this.pendingAmount
    if (!Number.isFinite(amount) || amount <= 0) return
    const token = this.currentToken || this.lastSession?.token || this.readToken()
    if (!token) return

    this.post(`/addresses/${this.addressIdValue}/pow_wow_update_session`, { token, amount })
      .then((data) => {
        if (!data.success) throw new Error(data.error || "Unable to sync saved amount.")
        this.pendingAmount = null
        this.clearPendingAmount()
        this.renderSession(data.session)
      })
      .catch((error) => this.setStatus(error.message))
  }

  hideSendingAnimation() {
    if (this.sendingTimer) {
      clearInterval(this.sendingTimer)
      this.sendingTimer = null
    }
    if (this.sendingTimeouts.length > 0) {
      this.sendingTimeouts.forEach((t) => clearTimeout(t))
      this.sendingTimeouts = []
    }
    if (this.hasSendingOverlayTarget) this.sendingOverlayTarget.style.display = "none"
    this.sendingVisualActive = false
  }

  showSendingAnimation() {
    if (!this.hasSendingOverlayTarget || !this.hasSendingImageTarget || !this.hasSendingTextTarget) return
    this.hideSendingAnimation()

    const steps = [
      { src: this.pinballImageUrlValue, text: "Charging pinball..." },
      { src: this.insertcoinsImageUrlValue, text: "Lightning handshake..." },
      { src: this.pigeonImageUrlValue, text: "Pigeon in flight..." }
    ].filter((f) => !!f.src)

    if (steps.length === 0) return

    this.sendingOverlayTarget.style.display = "flex"
    const setStep = (idx) => {
      const frame = steps[Math.min(idx, steps.length - 1)]
      this.sendingImageTarget.src = frame.src
      this.sendingTextTarget.textContent = frame.text
    }

    setStep(0)
    this.sendingTimeouts.push(setTimeout(() => setStep(1), 3000))
    this.sendingTimeouts.push(setTimeout(() => setStep(2), 6000))

    // Safety valve: never leave overlay stuck on screen.
    this.sendingTimeouts.push(setTimeout(() => {
      if (this.hasSendingTextTarget) this.sendingTextTarget.textContent = "Still sending... please wait."
      this.hideSendingAnimation()
    }, 15000))
  }
}
